
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import ActionButton from '@/components/ActionButton.vue';
import Alert from '@/components/Alert.vue';
import GoogleDriveSelector from '@/components/GoogleDriveSelector.vue';
import Selector from '@/components/OptionSelector.vue';
import Permission from '@/components/Permission.vue';
import FeatureUnlockModal from '@/components/FeatureUnlockModal.vue';
import { featureUnlockService } from '@/services/FeatureUnlockService';
import { FileDownloadList } from '@/collections/file_downloads';
import { FileDownloadModel } from '@/models/file_download';
import { get } from '@/services/http';

@Component({
  components: {
    ActionButton,
    Alert,
    Selector,
    GoogleDriveSelector,
    Permission,
    FeatureUnlockModal,
  },
})
export default class DownloadFullData extends Vue {
  public module: string = 'download_full_data';

  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => 0 })
  public reportTotal!: number;
  @Prop({ default: '' })
  public downloadName!: string;
  public localDownloadName: string = '';
  public downloadError: boolean = false;
  public downloadErrorMessage: string = '';
  public downloadSuccess: boolean = false;
  public downloadSuccessMessage: string = '';
  public downloadLoading: boolean = false;
  public emails: string[] = [];
  public merchantEmails: string[] = [];
  public selectedGoogleAccount: string = '';
  public selectedDriveFolder: string = '';
  public currentValueEmail: string | null = null;
  public fileFormats: string[] = ['csv', 'excel', 'pdf', 'google_sheets'];
  public selectedFileFormats: string[] = ['csv'];
  public deleteEmailDialog: boolean = false;
  public emailToDelete: string = '';
  public showRecentDownloads: boolean = true;
  public loadingRecentDownloads: boolean = false;
  public recentDownloads: FileDownloadModel[] = [];
  public canUnlockForFree: boolean = false;
  public unlockEligibilityChecked: boolean = false;
  public showUnlockModal: boolean = false;

  get isDownloadFull(): boolean {
    const rule = this.store.rules.items.find((item: any) => item.code === 'download_full_data');
    return !rule || rule.value !== 'current page';
  }

  get downloadRowLimit(): number {
    const limit = 50;
    return Math.min(this.reportTotal, limit);
  }

  public async created() {
    this.localDownloadName = this.downloadName || '';
    this.loadMerchantSavedEmails();
    // Reset recent downloads to ensure fresh data on each open
    this.recentDownloads = [];
    // Load recent downloads and check unlock eligibility in parallel
    const tasks: Promise<void>[] = [this.loadRecentDownloads()];
    if (!this.isDownloadFull) {
      tasks.push(this.checkUnlockEligibility());
    } else {
      this.unlockEligibilityChecked = true;
    }
    await Promise.all(tasks);
  }

  public mounted() {
    // Load recent downloads only when user opens the modal (removed automatic loading)
  }

  private async checkUnlockEligibility() {
    try {
      const status: any = await featureUnlockService.checkUnlockStatus(['download_full_data']);
      this.canUnlockForFree = !!status?.available?.includes('download_full_data');
    } catch (error) {
      // Non-critical — fall back to showing upgrade link
    } finally {
      this.unlockEligibilityChecked = true;
    }
  }

  public onFeatureUnlocked() {
    this.showUnlockModal = false;
    this.$emit('cancel-download');
    window.location.reload();
  }

  public loadMerchantSavedEmails() {
    const merchantEmailStr = localStorage.getItem('merchantEmails') || '';
    if (!merchantEmailStr) {
      if (this.store.config.contactEmail) {
        this.merchantEmails.push(this.store.config.contactEmail);
      }
    } else {
      this.merchantEmails = JSON.parse(merchantEmailStr);
      if (!this.merchantEmails || this.merchantEmails.length === 0) {
        if (this.store.config.contactEmail) {
          this.merchantEmails.push(this.store.config.contactEmail);
        }
      }
    }
    if (this.merchantEmails.length > 0 && !this.emails.includes(this.merchantEmails[this.merchantEmails.length - 1])) {
      this.emails.push(this.merchantEmails[this.merchantEmails.length - 1]);
    }
  }

  public async removeSavedEmail(email: string) {
    // Show confirmation dialog
    this.emailToDelete = email;
    this.deleteEmailDialog = true;
  }

  public confirmDeleteEmail() {
    const email = this.emailToDelete;
    // Remove from merchantEmails array
    const index = this.merchantEmails.indexOf(email);
    if (index > -1) {
      this.merchantEmails.splice(index, 1);
      // Force reactivity by reassigning the array
      this.merchantEmails = [...this.merchantEmails];
      // Update localStorage
      localStorage.setItem('merchantEmails', JSON.stringify(this.merchantEmails));

      // Also remove from selected emails if present
      const selectedIndex = this.emails.indexOf(email);
      if (selectedIndex > -1) {
        this.emails.splice(selectedIndex, 1);
        this.emails = [...this.emails];
      }
    }

    // Close dialog and reset
    this.deleteEmailDialog = false;
    this.emailToDelete = '';
  }

  public async download() {
    this.downloadLoading = true;
    // if (this.isLockedDownload) {
    //   return;
    // }

    if (this.currentValueEmail) {
      this.emails.push(this.currentValueEmail);
    }
    if (this.selectedFileFormats[0] === 'google_sheets') {
      this.emails = [this.selectedGoogleAccount];
    }

    if (this.emails.length === 0) {
      const errorMessage = `Please enter your email!`;
      this.downloadError = true;
      this.downloadErrorMessage = errorMessage;
      this.$emit('show-message', { type: 'error', message: errorMessage });
      this.downloadLoading = false;
      return;
    }

    for (const email of this.emails) {
      if (!this.validateEmail(email)) {
        const errorMessage = `"${email}" is not valid`;
        this.downloadError = true;
        this.downloadErrorMessage = errorMessage;
        this.$emit('show-message', { type: 'error', message: errorMessage });
        this.downloadLoading = false;
        return;
      }
    }
    this.saveMerchantEmail();

    this.$emit('startDownload', this.emails, this.selectedDriveFolder, this.localDownloadName);

    // Emit success message to parent
    const successMessage = `The exported file will be sent to the emails above`;
    this.$emit('show-message', { type: 'success', message: successMessage });

    this.downloadError = false;
    this.downloadErrorMessage = '';
    this.downloadSuccess = true;
    this.downloadSuccessMessage = successMessage;
    this.downloadLoading = false;
    
    // Close dialog after showing success message for 2 seconds
    setTimeout(() => {
      this.downloadSuccess = false;
      this.downloadSuccessMessage = '';
      this.cancel();
    }, 2000);
  }

  public cancel() {
    this.$emit('cancel-download');
  }

  public saveMerchantEmail() {
    for (const email of this.emails) {
      let exist = false;
      for (const savedEmail of this.merchantEmails) {
        if (email === savedEmail) {
          exist = true;
        }
      }
      if (!exist) {
        this.merchantEmails.push(email);
      }
    }
    localStorage.setItem('merchantEmails', JSON.stringify(this.merchantEmails));
  }

  public remove(item: string) {
    this.emails.splice(this.emails.indexOf(item), 1);
    this.emails = [...this.emails];
  }

  public updateSelectedGoogleAccount(account: string) {
    this.selectedGoogleAccount = account;
  }

  public updateSelectedDriveFolder(folder: string) {
    this.selectedDriveFolder = folder;
  }

  public updateEmails(currentValue: string) {
    this.currentValueEmail = currentValue;
  }

  public toggleEmailSelection(email: string) {
    if (this.emails.includes(email)) {
      this.emails.splice(this.emails.indexOf(email), 1);
    } else {
      this.emails.push(email);
    }
    this.emails = [...this.emails];
  }

  private validateEmail(email: string) {
    const emailRegexp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegexp.test(email);
  }


  @Watch('downloadName', { immediate: true })
  private onDownloadNameChanged(newVal: string) {
    this.localDownloadName = newVal || '';
  }

  @Watch('selectedFileFormats', { immediate: true, deep: true })
  private onSelectedFileFormatChanged(newVal: any) {
    this.$emit('updateDownloadFileExt', this.selectedFileFormats[0]);
  }


  public async toggleRecentDownloads() {
    this.showRecentDownloads = !this.showRecentDownloads;
  }

  public async refreshRecentDownloads() {
    // Clear current downloads to show loading state
    this.recentDownloads = [];
    await this.loadRecentDownloads();
  }

  public async loadRecentDownloads() {
    this.loadingRecentDownloads = true;
    try {
      const files = new FileDownloadList();
      await files.fetch();

      // Enrich files with measure and dimension names if available
      for (const file of files.items) {
        // The API should already provide basic measure/dimension info
        // but we can enhance it if we have access to full measure data
        if (this.$parent && (this.$parent as any).measures) {
          const measures = (this.$parent as any).measures;
          for (const measure of measures.items || []) {
            if (file.measure && file.measure.code === measure.code && measure.code) {
              file.measure = measure;
              // Also check dimensions within this measure
              if (measure.dimensions && measure.dimensions.items) {
                for (const dimension of measure.dimensions.items) {
                  if (file.dimension && file.dimension.code === dimension.code && dimension.code) {
                    file.dimension = dimension;
                    break;
                  }
                }
              }
              break;
            }
          }
        }
      }

      // Show up to 5 most recent downloads
      this.recentDownloads = files.items.slice(0, 5);
    } catch (error) {
      console.error('Failed to load recent downloads:', error);
      this.recentDownloads = [];
    } finally {
      this.loadingRecentDownloads = false;
    }
  }

  public formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes <= 1 ? 'Just now' : `${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    }
  }

  public getStatusColor(status: string): string {
    switch(status) {
      case 'EXPORTED': return 'success';
      case 'EXPORTING': return 'primary';
      case 'EXPIRED': return 'warning';
      case 'FAILED': return 'error';
      default: return 'grey';
    }
  }

  public getEffectiveStatus(file: FileDownloadModel): string {
    // Check if export has been running for more than 1 day
    if (file.status === 'EXPORTING' && file.createdAt) {
      const createdDate = new Date(file.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdDate.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      // If more than 4 hours, treat as expired
      if (diffHours >= 4) {
        return 'EXPIRED';
      }
    }
    return file.status || '';
  }

  public getDownloadDisplayName(file: FileDownloadModel): string {
    if (file.downloadName) {
      return file.downloadName;
    }

    // If no download name, build a name from measure and dimension
    let displayName = '';
    if (file.measure && file.measure.name) {
      displayName = file.measure.name;
      if (file.dimension && file.dimension.name) {
        displayName += ` - ${file.dimension.name}`;
      }
    } else if (file.measure && file.measure.code) {
      // Fallback to formatted code if name is not available
      displayName = this.formatCodeToReadable(file.measure.code);
      if (file.dimension && file.dimension.code) {
        displayName += ` - ${this.formatCodeToReadable(file.dimension.code)}`;
      }
    }

    return displayName || 'Unnamed Export';
  }

  private formatCodeToReadable(code: string): string {
    if (!code) return '';

    // Replace underscores with spaces
    let formatted = code.replace(/_/g, ' ');

    // Capitalize first letter of each word
    formatted = formatted.replace(/\b\w/g, (char) => char.toUpperCase());

    // Special handling for common prefixes
    formatted = formatted.replace(/^By\s/i, 'by ');
    formatted = formatted.replace(/^Per\s/i, 'per ');
    formatted = formatted.replace(/^With\s/i, 'with ');
    formatted = formatted.replace(/^In\s/i, 'in ');
    formatted = formatted.replace(/^At\s/i, 'at ');
    formatted = formatted.replace(/^On\s/i, 'on ');
    formatted = formatted.replace(/^For\s/i, 'for ');

    // Ensure first letter is always capitalized (except for special prefixes)
    if (!formatted.match(/^(by|per|with|in|at|on|for)\s/)) {
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    return formatted;
  }

  public async regenerateFile(file: FileDownloadModel) {
    try {
      await get('/regenerate', { fileId: file.id });
      file.status = 'EXPORTING';
      this.downloadSuccess = true;
      this.downloadSuccessMessage = 'Re-generating file. It will be sent to your email when ready.';
      
      // Hide success message after 3 seconds without blocking
      setTimeout(() => {
        this.downloadSuccess = false;
        this.downloadSuccessMessage = '';
      }, 3000);
    } catch (error) {
      this.downloadError = true;
      this.downloadErrorMessage = 'Failed to re-generate file. Please try again.';
    }
  }
}
